import { FileText, Users } from 'lucide-react';

import { Notificacoes } from '@/nucleo/navegacao/Notificacoes';
import { MobileNavMenu } from '@/ui/MobileNavMenu';
import { ThemeToggle } from '@/ui/ThemeToggle';

import { AmostrasDeAcesso } from './amostras/Acesso';
import { CASCA_REAL } from './cascaReal';
import { ComponentShowcase } from './ComponentShowcase';
import { ItemDaDocumentacao } from './ItemDaDocumentacao';
import { LivePreview } from './LivePreview';
import { Block, Para } from './print/PrintBlocks';
import { StructureDiagram } from './StructureDiagram';

export function NavegacaoTab() {
  return (
    <div className="space-y-6">
      <ItemDaDocumentacao id="casca-das-areas">
        <Block title="A casca das áreas internas">
          <Para>
            Toda tela de /operacao, /admin, /sme e /dre entra dentro da mesma casca, a{' '}
            <code>CascaDeArea</code>. O menu, a busca e as migalhas saem do mesmo mapa de navegação
            (<code>nucleo/navegacao/mapa.ts</code>) de onde o roteador tira as rotas, então menu e
            rota não divergem. A casca pede a sessão e a rota da área e fixa a barra lateral na
            janela; por isso a estrutura aparece aqui em diagrama, e só monta ao vivo o que monta
            sozinho.
          </Para>
          <StructureDiagram
            title="CascaDeArea — nucleo/navegacao/CascaDeArea.tsx"
            nodes={[
              {
                label: 'SkipToContent',
                detail: 'primeiro foco da página; leva a #conteudo',
              },
              {
                label: 'BarraLateral',
                detail: 'menu da área, gerado de mapa.ts',
              },
              {
                label: 'Cabeçalho',
                detail: 'fixo no topo',
                children: [
                  { label: 'SidebarTrigger', detail: 'recolhe e expande a barra lateral' },
                  { label: 'BuscaGlobal', detail: 'busca por tela; Ctrl+K ou ⌘K' },
                  {
                    label: 'GavetaDeNotificacoes',
                    detail: 'o sino (Notificacoes) e a gaveta que ele abre',
                  },
                  { label: 'ThemeToggle', detail: 'Claro, Escuro ou Sistema' },
                  {
                    label: 'MenuDaConta',
                    detail: 'quem está conectado, Meu perfil, outras áreas, Sair',
                  },
                  { label: 'Migalhas', detail: 'área › grupo › item › aba ou registro' },
                ],
              },
              {
                label: 'main#conteudo',
                detail: 'a tela da rota (Outlet), até 1400 px de largura',
              },
            ]}
          />
          <Para>
            O portal público tem casca própria (<code>CascaDoPortal</code>), com o{' '}
            <code>AcessibilidadeWidget</code> flutuante. No Portal do Credenciado e nas telas de
            detalhe, abaixo de 640 px, a navegação de volta é o <code>MobileNavMenu</code>.
          </Para>
        </Block>
      </ItemDaDocumentacao>

      <ItemDaDocumentacao id="componentes-da-navegacao">
        <Block title="Componentes da navegação e do portal">
          {/* Lista, não tabela: quatro colunas não cabem em 375 px sem rolar. */}
          <ul className="grid gap-3 md:grid-cols-2">
            {CASCA_REAL.map((c) => (
              <li key={c.nome} className="rounded-lg border border-border bg-card p-3 space-y-1">
                <p className="text-sm font-semibold font-mono text-foreground">{c.nome}</p>
                <p className="text-[11px] font-mono text-muted-foreground break-all">
                  apps/web/src/{c.arquivo}
                </p>
                <p className="text-xs text-muted-foreground">
                  Props:{' '}
                  <span className="font-mono text-foreground">
                    {c.props.length > 0 ? c.props.join(', ') : 'nenhuma'}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">{c.descricao}</p>
              </li>
            ))}
          </ul>
          <Para>
            O <code>AcessibilidadeWidget</code> e o <code>CookieConsent</code> não montam ao vivo
            aqui: o primeiro aplica as preferências guardadas à página inteira (fonte, alto
            contraste, movimento), e o segundo é uma faixa fixa que só aparece sem escolha gravada e
            grava a escolha ao fechar. Montados na documentação, mudariam a página de quem a lê e
            fechariam o aviso do portal de verdade. Os dois estão no portal público (
            <code>CascaDoPortal</code>).
          </Para>
        </Block>
      </ItemDaDocumentacao>

      <ComponentShowcase
        id="notificacoes"
        title="Notificacoes"
        description="O sino real do cabeçalho. A contagem vem de quem o monta (na casca, a GavetaDeNotificacoes); sem contagem, ou com zero, o número não aparece. O número é texto de 10 px em --destructive-foreground sobre --destructive, par que a aba Cores mede nos quatro temas (aparece como 'texto no botão destrutivo e no número do sino de notificações'). A amostra mostra o sino com a contagem."
        code={`import { Notificacoes } from '@/nucleo/navegacao/Notificacoes';

<Notificacoes contagem={naoLidas} />`}
        propsTable={[
          [
            'contagem',
            'number',
            '-',
            'Não lidas; acima de 99 aparece "99+". Ausente ou zero: sem número.',
          ],
          ['...resto', 'props de <button>', '-', 'Repassadas ao botão (é gatilho com asChild).'],
        ]}
      >
        <div className="flex items-center gap-6">
          <Notificacoes />
          <Notificacoes contagem={3} />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="theme-toggle"
        title="ThemeToggle"
        description="O alternador real, o mesmo do cabeçalho: Claro, Escuro ou Sistema. A escolha vale para a aplicação inteira e fica guardada neste navegador (next-themes, chave teg-theme)."
        code={`import { ThemeToggle } from '@/ui/ThemeToggle';

<ThemeToggle />`}
        propsTable={[['(nenhuma)', '-', '-', 'Lê e grava o tema pelo next-themes']]}
      >
        <ThemeToggle />
      </ComponentShowcase>

      <ComponentShowcase
        id="mobile-nav-menu"
        title="MobileNavMenu"
        description="Barra compacta das telas de detalhe no celular. Só aparece com a janela abaixo de 640 px (sm:hidden): no computador o palco abaixo fica vazio de propósito; abra esta página no celular para vê-la."
        code={`import { MobileNavMenu } from '@/ui/MobileNavMenu';

<MobileNavMenu
  title="Termo de Adesão"
  backTo="/credenciado/contratos"
  backLabel="Contratos"
  links={[{ label: 'Documentos', href: '/credenciado/documentos', icon: FileText }]}
/>`}
        propsTable={[
          ['backTo', 'string', '-', 'Destino do "Voltar"'],
          ['backLabel', 'string', '"Voltar"', 'Rótulo do botão Voltar'],
          ['title', 'string', '-', 'Rótulo curto no centro'],
          ['links', 'MobileNavLink[]', '-', 'Itens do menu ⋯; sem itens, o ⋯ não aparece'],
          ['menuLabel', 'string', '"Navegação"', 'Título da seção do menu'],
          ['menuAriaLabel', 'string', '"Mais ações"', 'Rótulo acessível do botão ⋯'],
        ]}
      >
        <LivePreview width="mobile" surface="card">
          <MobileNavMenu
            title="Termo de Adesão"
            backTo="/admin/design-system"
            backLabel="Contratos"
            links={[
              { label: 'Documentos', href: '/admin/design-system', icon: FileText },
              { label: 'Equipe', href: '/admin/design-system', icon: Users },
            ]}
          />
        </LivePreview>
      </ComponentShowcase>

      <AmostrasDeAcesso />
    </div>
  );
}
